import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { invitationApi, invitationError } from '../services/classInvitationService';

export default function TeacherClassInvitations({ groupId, groupName }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [requests, setRequests] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [qr, setQr] = useState('');
  const [confirm, setConfirm] = useState('');
  const link = invitation?.token
    ? `${window.location.origin}/student/join/${invitation.token}`
    : '';
  const expired = invitation?.expiresAt && invitation.expiresAt <= Date.now();
  const active = invitation?.active && !expired;

  const loadRequests = useCallback(
    async (nextCursor = null) => {
      const result = await invitationApi('pending', { groupId, cursor: nextCursor });
      setRequests((previous) => (nextCursor ? [...previous, ...result.requests] : result.requests));
      setCursor(result.nextCursor);
    },
    [groupId]
  );

  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    setLoaded(false);
    setBusy(true);
    setError('');
    setNotice('');
    Promise.all([invitationApi('settings', { groupId }), invitationApi('pending', { groupId })])
      .then(([settings, pending]) => {
        if (!current) return;
        setInvitation(settings.invitation);
        setApprovalRequired(settings.invitation?.approvalRequired || false);
        setExpiresInDays(settings.invitation?.expiresInDays ?? 30);
        setRequests(pending.requests);
        setCursor(pending.nextCursor);
        setLoaded(true);
      })
      .catch((e) => {
        if (current) setError(invitationError(e, t));
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [open, groupId, t, loadAttempt]);

  useEffect(() => {
    let current = true;
    setQr('');
    if (link && active)
      QRCode.toDataURL(link, {
        width: 640,
        margin: 4,
        errorCorrectionLevel: 'M',
        color: { dark: '#101a33', light: '#ffffff' },
      })
        .then((value) => {
          if (current) setQr(value);
        })
        .catch(() => {
          if (current) setError(t('Unable to generate the QR code. Please reopen this dialog.'));
        });
    return () => {
      current = false;
    };
  }, [link, active, t]);

  const save = async (action) => {
    setBusy(true);
    setError('');
    setNotice('');
    setConfirm('');
    try {
      const result = await invitationApi('save', {
        groupId,
        action,
        approvalRequired,
        expiresInDays,
      });
      setInvitation(result.invitation);
      setNotice(t('Invitation settings saved.'));
    } catch (e) {
      setError(invitationError(e, t));
    } finally {
      setBusy(false);
    }
  };
  const review = async (studentUid, decision) => {
    setBusy(true);
    setError('');
    try {
      await invitationApi('review', { groupId, studentUid, decision });
      await loadRequests();
      setNotice(t(decision === 'approve' ? 'Student enrolled successfully.' : 'Request declined.'));
    } catch (e) {
      setError(invitationError(e, t));
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(t('Copied to clipboard.'));
    } catch (_) {
      setError(t('Copy is unavailable. Select and copy the invitation link below.'));
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<QrCode2RoundedIcon />}
        onClick={() => setOpen(true)}
        sx={{ my: 1.5, textTransform: 'none', borderRadius: 2 }}
      >
        {t('Invite students')}
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ dir: i18n.dir(), sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          {t('Invite students')}
          <Typography component="p" variant="body2" color="text.secondary">
            {groupName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('Share a link or QR code. Students create their own account and join this class.')}
            </Typography>
            {error && (
              <Alert
                severity="error"
                action={
                  !loaded && !busy ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setLoadAttempt((value) => value + 1)}
                    >
                      {t('Retry')}
                    </Button>
                  ) : undefined
                }
              >
                {error}
              </Alert>
            )}
            {notice && <Alert severity="success">{notice}</Alert>}
            {busy && (
              <Box role="status" aria-label={t('Loading')} sx={{ textAlign: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {loaded && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={approvalRequired}
                      onChange={(e) => setApprovalRequired(e.target.checked)}
                      disabled={busy}
                    />
                  }
                  label={t('Require my approval before enrollment')}
                />
                <TextField
                  select
                  size="small"
                  label={t('Invitation expires in')}
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                  disabled={busy}
                >
                  {[1, 7, 30, 90].map((days) => (
                    <MenuItem key={days} value={days}>
                      {t('invitationDays', { count: days, defaultValue: '{{count}} days' })}
                    </MenuItem>
                  ))}
                  <MenuItem value={0}>{t('No expiry')}</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={() =>
                    save(!invitation ? 'create' : invitation.active ? 'update' : 'rotate')
                  }
                >
                  {t(active ? 'Save settings' : 'Enable invitations')}
                </Button>
                {invitation && (
                  <Typography variant="caption" color="text.secondary">
                    {!invitation.active
                      ? t('Invitation disabled')
                      : expired
                        ? t('Invitation expired')
                        : invitation.expiresAt
                          ? `${t('Expires')}: ${new Date(invitation.expiresAt).toLocaleDateString(i18n.language)}`
                          : t('No expiry')}
                  </Typography>
                )}
                {active && (
                  <>
                    <TextField
                      label={t('Invitation link')}
                      value={link}
                      size="small"
                      InputProps={{ readOnly: true }}
                      inputProps={{ dir: 'ltr', onFocus: (e) => e.target.select() }}
                    />
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Button
                        size="small"
                        startIcon={<ContentCopyRoundedIcon />}
                        onClick={() => copy(link)}
                      >
                        {t('Copy link')}
                      </Button>
                      <Button size="small" onClick={() => copy(invitation.token)}>
                        {t('Copy invitation code')}
                      </Button>
                    </Stack>
                    {qr && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Box
                          component="img"
                          src={qr}
                          alt={t('Scan to join this class')}
                          sx={{ width: 220, maxWidth: '100%', display: 'block', mx: 'auto' }}
                        />
                        <Button
                          component="a"
                          href={qr}
                          download={`class-${groupId}-invitation.png`}
                          startIcon={<DownloadRoundedIcon />}
                        >
                          {t('Download QR code')}
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t(
                            'Anyone with this invitation can request to join. Share it with your students only.'
                          )}
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1}>
                      <Button
                        color="warning"
                        size="small"
                        disabled={busy}
                        onClick={() => setConfirm('rotate')}
                      >
                        {t('Replace link and QR code')}
                      </Button>
                      <Button
                        color="error"
                        size="small"
                        disabled={busy}
                        onClick={() => setConfirm('disable')}
                      >
                        {t('Disable invitation')}
                      </Button>
                    </Stack>
                  </>
                )}
                {confirm && (
                  <Alert
                    severity="warning"
                    action={
                      <Button color="inherit" disabled={busy} onClick={() => save(confirm)}>
                        {t('Confirm')}
                      </Button>
                    }
                    onClose={() => setConfirm('')}
                  >
                    {t(
                      'The current link and QR code will stop working. Students already enrolled keep their access.'
                    )}
                  </Alert>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={600}>{t('Pending requests')}</Typography>
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await loadRequests();
                      } catch (e) {
                        setError(invitationError(e, t));
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {t('Refresh')}
                  </Button>
                </Stack>
                {!requests.length && (
                  <Typography variant="body2" color="text.secondary">
                    {t('No pending requests.')}
                  </Typography>
                )}
                {requests.map((request) => (
                  <Box
                    key={request.id}
                    sx={{ p: 1.5, border: '1px solid #e6eaf2', borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {request.firstName} {request.lastName}
                    </Typography>
                    <Typography variant="caption" component="p" dir="ltr">
                      {request.email}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy}
                        onClick={() => review(request.uid, 'approve')}
                      >
                        {t('Approve')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() => review(request.uid, 'reject')}
                      >
                        {t('Decline')}
                      </Button>
                    </Stack>
                  </Box>
                ))}
                {cursor && (
                  <Button
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await loadRequests(cursor);
                      } catch (e) {
                        setError(invitationError(e, t));
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {t('Load more')}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setOpen(false)}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
